import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button, Modal, Pill } from '@jywer/ui';
import { fetchAdminOrder, patchAdminOrder, voidAdminOrder } from './api';
import { ContractPanel } from './ContractPanel';
import type { AdminOrderDetail } from './types';

export function OrderEditPage() {
  const params = useParams<{ id: string }>();
  const orderId = params.id ?? '';

  const [order, setOrder] = useState<AdminOrderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [voidOpen, setVoidOpen] = useState(false);
  const [voidReason, setVoidReason] = useState('');

  // Edit-form local state (mirrors editable fields).
  const [ordererName, setOrdererName] = useState('');
  const [ordererEmail, setOrdererEmail] = useState('');
  const [ordererPhone, setOrdererPhone] = useState('');
  const [orderSummary, setOrderSummary] = useState('');
  const [pipelineTaskId, setPipelineTaskId] = useState('');
  const [crossSales, setCrossSales] = useState('');
  const [marketingIntel, setMarketingIntel] = useState('');
  const [extraRepId, setExtraRepId] = useState<string>('');
  const [extraSharePct, setExtraSharePct] = useState<25 | 50 | 75 | ''>('');

  const seedForm = useCallback((o: AdminOrderDetail) => {
    setOrdererName(o.orderer_name ?? '');
    setOrdererEmail(o.orderer_email ?? '');
    setOrdererPhone(o.orderer_phone ?? '');
    setOrderSummary(o.order_summary_md ?? '');
    setPipelineTaskId(o.pipeline_clickup_task_id ?? '');
    setCrossSales(o.add_on_cross_sales_notes ?? '');
    setMarketingIntel(o.client_marketing_intel ?? '');
    setExtraRepId(o.extra_sales_rep_id ?? '');
    setExtraSharePct((o.extra_sales_rep_share_pct ?? '') as 25 | 50 | 75 | '');
  }, []);

  const load = useCallback(async () => {
    if (!orderId) return;
    setLoading(true);
    setErr(null);
    try {
      const o = await fetchAdminOrder(orderId);
      setOrder(o);
      seedForm(o);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Kunne ikke hente ordre');
    } finally {
      setLoading(false);
    }
  }, [orderId, seedForm]);

  useEffect(() => {
    void load();
  }, [load]);

  const onSave = async () => {
    if (!orderId) return;
    setSaving(true);
    setErr(null);
    try {
      const patch: Parameters<typeof patchAdminOrder>[1] = {
        orderer_name: ordererName || null,
        orderer_email: ordererEmail || null,
        orderer_phone: ordererPhone || null,
        order_summary_md: orderSummary || null,
        pipeline_clickup_task_id: pipelineTaskId || null,
        add_on_cross_sales_notes: crossSales || null,
        client_marketing_intel: marketingIntel || null,
      };
      // Extra rep + pct paired (validated server-side too).
      const hasRep = !!extraRepId;
      const hasPct = extraSharePct !== '';
      if (hasRep || hasPct) {
        patch.extra_sales_rep_id = extraRepId || null;
        patch.extra_sales_rep_share_pct = hasPct ? (extraSharePct as 25 | 50 | 75) : null;
      }
      const updated = await patchAdminOrder(orderId, patch);
      setOrder(updated);
      seedForm(updated);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Opdatering fejlede');
    } finally {
      setSaving(false);
    }
  };

  const onVoidConfirm = async () => {
    if (!orderId || voidReason.trim().length === 0) return;
    setSaving(true);
    setErr(null);
    try {
      const updated = await voidAdminOrder(orderId, voidReason.trim());
      setOrder(updated);
      setVoidOpen(false);
      setVoidReason('');
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Annullering fejlede');
    } finally {
      setSaving(false);
    }
  };

  if (loading && !order) {
    return (
      <div className="rounded border border-line-1 bg-paper-0 px-4 py-10 text-center text-ink-2">
        Henter ordre…
      </div>
    );
  }
  if (!order) {
    return (
      <div className="flex flex-col gap-3">
        <Link to="/orders" className="text-sm text-primary hover:underline">
          ← Tilbage til ordrer
        </Link>
        <div className="rounded border border-status-err/40 bg-status-err/5 px-4 py-3 text-sm text-status-err">
          {err ?? 'Ordre ikke fundet'}
        </div>
      </div>
    );
  }

  const isVoided = order.is_voided;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <Link to="/orders" className="text-sm text-primary hover:underline">
          ← Tilbage til ordrer
        </Link>
        <Pill variant={isVoided ? 'muted' : 'success'}>{isVoided ? 'Annulleret' : 'Aktiv'}</Pill>
      </div>

      <div>
        <h1 className="text-2xl font-semibold text-primary">{order.client_name}</h1>
        <p className="text-sm text-ink-2">
          Ordre registreret {new Date(order.registered_at).toLocaleString('da-DK')} af{' '}
          {order.sales_rep_name ?? '—'}
          {order.extra_sales_rep_name
            ? ` + ${order.extra_sales_rep_name} (${order.extra_sales_rep_share_pct ?? 0}%)`
            : ''}
        </p>
      </div>

      {err ? (
        <div className="rounded border border-status-err/40 bg-status-err/5 px-4 py-3 text-sm text-status-err">
          {err}
        </div>
      ) : null}

      {isVoided ? (
        <div className="rounded border border-line-1 bg-paper-1 px-4 py-3 text-sm text-ink-2">
          <strong className="text-ink-0">Annulleret:</strong> {order.void_reason ?? '—'}
          {order.void_at ? ` · ${new Date(order.void_at).toLocaleString('da-DK')}` : ''}
        </div>
      ) : null}

      <div className="grid gap-4 md:grid-cols-2">
        <section className="flex flex-col gap-3 rounded border border-line-1 bg-paper-0 p-4">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-2">
            Kontaktperson
          </h2>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-xs text-ink-2">Navn</span>
            <input
              type="text"
              value={ordererName}
              onChange={(e) => setOrdererName(e.target.value)}
              disabled={isVoided}
              maxLength={200}
              className="rounded border border-line-2 px-2.5 py-1.5 disabled:bg-paper-1"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-xs text-ink-2">Email</span>
            <input
              type="email"
              value={ordererEmail}
              onChange={(e) => setOrdererEmail(e.target.value)}
              disabled={isVoided}
              maxLength={320}
              className="rounded border border-line-2 px-2.5 py-1.5 disabled:bg-paper-1"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-xs text-ink-2">Telefon</span>
            <input
              type="tel"
              value={ordererPhone}
              onChange={(e) => setOrdererPhone(e.target.value)}
              disabled={isVoided}
              maxLength={64}
              className="rounded border border-line-2 px-2.5 py-1.5 disabled:bg-paper-1"
            />
          </label>

          <h2 className="mt-4 text-sm font-semibold uppercase tracking-wide text-ink-2">
            Salg-noter
          </h2>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-xs text-ink-2">Order summary (Markdown)</span>
            <textarea
              value={orderSummary}
              onChange={(e) => setOrderSummary(e.target.value)}
              disabled={isVoided}
              maxLength={20_000}
              rows={6}
              className="rounded border border-line-2 px-2.5 py-1.5 font-mono text-xs disabled:bg-paper-1"
            />
          </label>
          {/* Read-only legacy id. Display-only for historic rows — no new
              entry. The DB column + intake PATCH path are retained
              (CLICKUP-7 drops columns last); the value is no longer editable. */}
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-xs text-ink-2">Pipeline-task ID (historisk)</span>
            <input
              type="text"
              value={pipelineTaskId || '—'}
              readOnly
              disabled
              maxLength={64}
              className="rounded border border-line-2 px-2.5 py-1.5 font-mono text-xs text-ink-2 disabled:bg-paper-1"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-xs text-ink-2">Add-on / krydssalg-noter</span>
            <textarea
              value={crossSales}
              onChange={(e) => setCrossSales(e.target.value)}
              disabled={isVoided}
              maxLength={10_000}
              rows={3}
              className="rounded border border-line-2 px-2.5 py-1.5 text-xs disabled:bg-paper-1"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-xs text-ink-2">Marketing-intel om kunden</span>
            <textarea
              value={marketingIntel}
              onChange={(e) => setMarketingIntel(e.target.value)}
              disabled={isVoided}
              maxLength={10_000}
              rows={3}
              className="rounded border border-line-2 px-2.5 py-1.5 text-xs disabled:bg-paper-1"
            />
          </label>
        </section>

        <section className="flex flex-col gap-3">
          <ContractPanel
            order={order}
            onUpdated={(updated) => {
              setOrder(updated);
              seedForm(updated);
            }}
            disabled={isVoided}
          />

          <div className="flex flex-col gap-3 rounded border border-line-1 bg-paper-0 p-4">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-2">
              Sælger-split
            </h2>
            <p className="text-xs text-ink-2">
              Primær sælger ({order.sales_rep_name ?? '—'}) ændres ikke her — kun extra-rep + andel.
            </p>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-xs text-ink-2">Extra-rep ID (UUID)</span>
              <input
                type="text"
                value={extraRepId}
                onChange={(e) => setExtraRepId(e.target.value.trim())}
                disabled={isVoided}
                placeholder="11111111-2222-3333-4444-555555555555"
                className="rounded border border-line-2 px-2.5 py-1.5 font-mono text-xs disabled:bg-paper-1"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-xs text-ink-2">Andel</span>
              <select
                value={String(extraSharePct)}
                onChange={(e) =>
                  setExtraSharePct(
                    e.target.value === '' ? '' : (Number(e.target.value) as 25 | 50 | 75),
                  )
                }
                disabled={isVoided}
                className="rounded border border-line-2 px-2.5 py-1.5 disabled:bg-paper-1"
              >
                <option value="">— Ingen extra-rep —</option>
                <option value="25">25 %</option>
                <option value="50">50 %</option>
                <option value="75">75 %</option>
              </select>
            </label>
          </div>

          <div className="rounded border border-line-1 bg-paper-0 p-4">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-2">Produkter</h2>
            <p className="mt-2 text-sm text-ink-1">{order.product_summary}</p>
            <p className="mt-1 text-xs text-ink-2">Total: {order.total_value} DKK</p>
            <p className="mt-2 text-xs text-ink-2">
              Rediger projekter / abonnementer i deres respektive views.
            </p>
          </div>
        </section>
      </div>

      <div className="flex items-center justify-end gap-2 border-t border-line-1 pt-4">
        {isVoided ? null : (
          <Button variant="destructive" onClick={() => setVoidOpen(true)} disabled={saving}>
            Annuller ordre
          </Button>
        )}
        <Button onClick={onSave} disabled={isVoided || saving}>
          {saving ? 'Gemmer…' : 'Gem ændringer'}
        </Button>
      </div>

      <Modal
        open={voidOpen}
        onClose={() => setVoidOpen(false)}
        title="Annuller ordre"
        subtitle="Annulleringen er endelig — ordren fjernes ikke, men markeres som annulleret."
      >
        <div className="flex flex-col gap-3 p-4">
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-xs text-ink-2">Begrundelse (vises i revisionssporet)</span>
            <textarea
              value={voidReason}
              onChange={(e) => setVoidReason(e.target.value)}
              maxLength={1000}
              rows={3}
              className="rounded border border-line-2 px-2.5 py-1.5 text-sm"
            />
          </label>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setVoidOpen(false)}>
              Fortryd
            </Button>
            <Button
              variant="destructive"
              onClick={onVoidConfirm}
              disabled={voidReason.trim().length === 0 || saving}
            >
              {saving ? 'Annullerer…' : 'Bekræft annullering'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
